import React, { FC, useRef, useState } from 'react';
import { Button, ButtonStyles, Popup, Tabs } from '@silverfort/sf-components';
import {
    hasContentSingleDataProps,
    hasContentTabsProps,
    hasPopupSingleDataProps,
    hasPopupTabsProps,
    TabValue,
    TopMetricPopupContentProps,
    TopMetricPopupProps
} from '../../types';
import TopMetricTable from './TopMetricTable/TopMetricTable';
import useGetRefSize from '../../../../hooks/useGetRefSize';

interface FooterProps {
    onClose: () => void;
}

const Content = <T extends TabValue>(props: TopMetricPopupContentProps<T>) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const initialSelectedTab = hasContentTabsProps(props) ? props.selectedTab : undefined;
    const [selectedTab, setSelectedTab] = useState<T | undefined>(initialSelectedTab);

    const { height } = useGetRefSize(tableContainerRef);

    if (hasContentSingleDataProps(props)) {
        return (
            <div ref={tableContainerRef} className="pt-4 flex flex-col gap-4 h-full">
                <TopMetricTable singleData={props.singleData} maxHeight={height ?? 0} />
            </div>
        );
    }

    if (hasContentTabsProps(props)) {
        const currentSelectedTab = selectedTab ?? props.selectedTab;

        return (
            <div className="pt-2 flex flex-col gap-4 h-full">
                {props.tabs.length > 1 && (
                    <Tabs
                        selectedTab={currentSelectedTab}
                        onClick={(value) => setSelectedTab(value as T)}
                        tabs={props.tabs.map((tab) => ({
                            value: tab.value,
                            name: tab.name
                        }))}
                    />
                )}
                <div ref={tableContainerRef} className="flex-1">
                    <TopMetricTable<T> selectedTab={currentSelectedTab} tabs={props.tabs} maxHeight={height ?? 0} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 h-full">
            <div className="flex items-center justify-center h-full text-gray-500">No data available</div>
        </div>
    );
};

const Footer: FC<FooterProps> = ({ onClose }) => (
    <div className="gap-2 flex justify-end items-center">
        <Button buttonStyle={ButtonStyles.PrimarySolid} text="Done" onClick={onClose} />
    </div>
);

const TopMetricPopup = <T extends TabValue>(props: TopMetricPopupProps<T>) => {
    const { open, onClose, title } = props;

    let contentProps: TopMetricPopupContentProps<T>;

    if (hasPopupSingleDataProps(props)) {
        contentProps = { singleData: props.singleData };
    } else if (hasPopupTabsProps(props)) {
        contentProps = { selectedTab: props.selectedTab, tabs: props.tabs };
    } else {
        // This should never happen with proper typing, but TypeScript requires it
        throw new Error('Invalid props configuration for TopMetricPopup');
    }

    return (
        <Popup
            open={open}
            title={title}
            popupClassName="w-[870px] max-w-[870px] h-[680px]"
            contentClassName="px-4 pb-4 !pt-0"
            onClose={onClose}
            ContentComponent={Content}
            FooterComponent={Footer}
            ContentComponentProps={contentProps}
            FooterComponentProps={{ onClose }}
            sx={{
                zIndex: 10001 // Temporary override to ensure popup appears above everything because of tower layout issues
            }}
        />
    );
};

export default TopMetricPopup;
